import type { KeyboardEvent, ReactNode } from 'react';

export interface TabOption<T extends string> {
  value: T;
  label: ReactNode;
}

interface TabsProps<T extends string> {
  ariaLabel: string;
  idPrefix: string;
  options: readonly TabOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}

/** Shared horizontal section switcher with complete keyboard navigation. */
export function Tabs<T extends string>({
  ariaLabel,
  idPrefix,
  options,
  value,
  onChange,
  className = '',
}: TabsProps<T>) {
  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const values = options.map((option) => option.value);
    const current = values.indexOf(value);
    let next: number | null = null;
    if (event.key === 'ArrowRight') next = (current + 1) % values.length;
    if (event.key === 'ArrowLeft') next = (current - 1 + values.length) % values.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = values.length - 1;
    if (next === null) return;

    event.preventDefault();
    const nextValue = values[next];
    onChange(nextValue);
    requestAnimationFrame(() => document.getElementById(`${idPrefix}-tab-${nextValue}`)?.focus());
  }

  return (
    <div className={`tabs${className ? ` ${className}` : ''}`} role="tablist" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          type="button"
          role="tab"
          className="tab"
          id={`${idPrefix}-tab-${option.value}`}
          key={option.value}
          aria-selected={value === option.value}
          aria-controls={`${idPrefix}-panel-${option.value}`}
          tabIndex={value === option.value ? 0 : -1}
          onClick={() => onChange(option.value)}
          onKeyDown={onKeyDown}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
